/**
 * Typed client for /api/v1/workshop/* (the workshop v2 read models + the lapsed-order actions).
 *
 * Uses the app's one axios instance (config/api: base URL, Bearer token, 401 handling). Every action that is not a
 * read model (confirm, unconfirm, return, receive, pay, invoice, chat, add items …) keeps calling the EXISTING
 * endpoints through services/*.ts, so the business rules stay on the server.
 *
 * The server answers 403 when the caller is not a WORKSHOP login with «الواجهة الجديدة للورشة» on.
 */
import { api } from '../config/api';
import type {
  WsPulse, WsHomeResponse, WsSearchResponse, WsOrdersQuery, WsOrdersListResponse, WsOrderSummary,
  WsRestoreLapsedResponse, WsRequoteResponse,
} from './contracts';

export * from './contracts';

const BASE = '/workshop';

/** Drop undefined / empty values so the query string only carries real filters. */
function params<T extends object>(q: T | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!q) return out;
  for (const [k, v] of Object.entries(q as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = typeof v === 'boolean' ? (v ? '1' : '0') : String(v);
  }
  return out;
}

export const workshopApi = {
  /** Nav badges — poll with usePulse() rather than calling this directly. */
  pulse: (signal?: AbortSignal) =>
    api.get<WsPulse>(`${BASE}/pulse`, { signal }).then((r) => r.data),

  /** `fresh` skips the server's 60 s per-workshop cache — after an action, a pull-to-refresh or a pulse change. */
  home: (signal?: AbortSignal, fresh = false) =>
    api.get<WsHomeResponse>(`${BASE}/home`, { signal, params: fresh ? { fresh: '1' } : undefined }).then((r) => r.data),

  search: (q: string, signal?: AbortSignal) =>
    api.get<WsSearchResponse>(`${BASE}/search`, { params: { q }, signal }).then((r) => r.data),

  orders: (query?: WsOrdersQuery, signal?: AbortSignal) =>
    api.get<WsOrdersListResponse>(`${BASE}/orders`, { params: params(query), signal }).then((r) => r.data),

  orderSummary: (id: string, signal?: AbortSignal) =>
    api.get<WsOrderSummary>(`${BASE}/orders/${encodeURIComponent(id)}/summary`, { signal }).then((r) => r.data),

  /** «إلغاء الانتهاء» — lapsed ≤ 30 days: the same order back with its prices. */
  restoreLapsed: (id: string) =>
    api.post<WsRestoreLapsedResponse>(`${BASE}/orders/${encodeURIComponent(id)}/restore`).then((r) => r.data),

  /** «أعد الطلب» — the items into a NEW order for fresh prices. */
  requote: (id: string) =>
    api.post<WsRequoteResponse>(`${BASE}/orders/${encodeURIComponent(id)}/requote`).then((r) => r.data),
};

/** The server's error message (Arabic), or null — for ErrorState / toasts. */
export function apiErrorMessage(e: unknown): string | null {
  const err = e as { response?: { data?: { error?: { message?: string }; message?: string } } };
  return err?.response?.data?.error?.message || err?.response?.data?.message || null;
}

/** The server's error code (e.g. 'LAPSE_TOO_OLD'), or null. */
export function apiErrorCode(e: unknown): string | null {
  const err = e as { response?: { data?: { error?: { code?: string } } } };
  return err?.response?.data?.error?.code || null;
}

/** True for an aborted request (unmount / a newer request) — never show it as an error. */
export function isAbort(e: unknown): boolean {
  const err = e as { name?: string; code?: string };
  return err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';
}
